import { Category, ProductType } from '@bookstore/shared';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BookOpen, Package, Grid3x3, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import SubCategoryBadge from '@/components/common/SubCategoryBadge';

interface CategoryCardProps {
  category: Category;
  childrenCategories?: Category[];
  index?: number;
}

const CategoryCard = ({ category, childrenCategories = [], index = 0 }: CategoryCardProps) => {
  const getCategoryColor = (cat: Category) => {
    if (cat.productType === ProductType.BOOK) {
      return {
        gradient: 'from-primary-600/90 via-primary-800/90 to-primary-900',
        iconBg: 'bg-primary-100/20',
        accentColor: 'text-primary-100',
        hoverRing: 'group-hover:ring-primary-400/50 hover:ring-primary-600/50',
      };
    }
    if (cat.productType === ProductType.STATIONERY) {
      return {
        gradient: 'from-amber-500/90 via-orange-500/90 to-red-500/90',
        iconBg: 'bg-amber-500/20',
        accentColor: 'text-amber-100',
        hoverRing: 'group-hover:ring-amber-400/50 hover:ring-amber-600/50',
      };
    }
    return {
      gradient: 'from-slate-600/90 via-slate-700/90 to-slate-800/90',
      iconBg: 'bg-slate-500/20',
      accentColor: 'text-slate-100',
      hoverRing: 'group-hover:ring-slate-400/50 hover:ring-slate-600/50',
    };
  };

  const getCategoryIcon = (cat: Category) => {
    if (cat.productType === ProductType.BOOK) return BookOpen;
    if (cat.productType === ProductType.STATIONERY) return Package;
    return Grid3x3;
  };

  const Icon = getCategoryIcon(category);
  const colors = getCategoryColor(category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <Link
        to={`/products/${category.seoUrl}`}
        className={`group relative block rounded-2xl overflow-hidden bg-white shadow-md border border-gray-200/50 hover:shadow-2xl hover:scale-[1.02] transition-all duration-500 ring-0 hover:ring-4 ${colors.hoverRing}`}
      >
        {/* Gradient Background Layer */}
        <div className="relative h-56 overflow-hidden">
          {/* Animated Gradient */}
          <div
            className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} `}
          />

          {/* Optional Image with Better Blend */}
          {category.imageUrl && (
            <img
              src={category.imageUrl}
              alt={category.name}
              className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-soft-light "
            />
          )}

          {/* Decorative Pattern Overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl transform translate-x-16 -translate-y-16" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full blur-2xl transform -translate-x-12 translate-y-12" />
          </div>

          {/* Content Overlay */}
          <div className="relative h-full flex flex-col justify-between p-6">
            {/* Top Section: Icon + Type Badge */}
            <div className="flex items-start justify-between">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className={`p-3.5 ${colors.iconBg} rounded-2xl backdrop-blur-xl border border-white/30 shadow-lg`}
              >
                <Icon className="w-7 h-7 text-white drop-shadow-lg" />
              </motion.div>

              {/* Floating Arrow */}
              <motion.div
                className="p-2 bg-white/20 rounded-full backdrop-blur-md border border-white/40"
                whileHover={{ scale: 1.1, x: 4, y: -4 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <ArrowUpRight className="w-5 h-5 text-white" />
              </motion.div>
            </div>

            {/* Bottom Section: Title + Description */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-white/80" />
                <span className="text-xs font-medium text-white/90 uppercase tracking-wider">
                  {category.productType === ProductType.BOOK ? 'Books' : 'Stationery'}
                </span>
              </div>
              
              <h2 className="text-2xl font-bold text-white tracking-tight leading-tight drop-shadow-lg">
                {category.name}
              </h2>

              {category.description && (
                <p className="text-sm text-white/90 line-clamp-2 leading-relaxed">
                  {category.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Child Categories Section */}
        {childrenCategories.length > 0 && (
          <div className="bg-gradient-to-br from-gray-50 to-white p-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-4 bg-gradient-to-b from-primary-500 to-primary-700 rounded-full" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Explore Subcategories
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {childrenCategories.slice(0, 5).map((child) => (
                <SubCategoryBadge key={child.id} subCategory={child} />
              ))}
              {childrenCategories.length > 5 && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 px-3 py-1.5 bg-gray-100 rounded-full">
                  <Sparkles className="w-3 h-3" />
                  +{childrenCategories.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </Link>
    </motion.div>
  );
};

export default CategoryCard;
